import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { processingApi, organizationApi, serviceProviderApi, openPdfExport } from '../services/api';
import {
  ProcessingActivity,
  Organization,
  ProcessingStatus,
  LegalBasis,
  DataCategory,
  DataSubjectCategory,
  RiskLevel,
  ServiceProvider,
  ServiceProviderRole,
  AvvStatus
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  TabContainer,
  Tab,
  MessageBox,
  ObjectStatus,
  Icon,
  BusyIndicator,
  MessageStrip,
  Table,
  TableColumn,
  TableRow,
  TableCell
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import QuickCreateDialog from '../components/QuickCreateDialog';
import { formatRetention } from '../helpers/formatRetention';

const legalBasisLabels: Record<LegalBasis, string> = {
  [LegalBasis.CONSENT]: 'Einwilligung (Art. 6 Abs. 1 lit. a)',
  [LegalBasis.CONTRACT]: 'Vertragserfüllung (Art. 6 Abs. 1 lit. b)',
  [LegalBasis.LEGAL_OBLIGATION]: 'Rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c)',
  [LegalBasis.VITAL_INTERESTS]: 'Lebenswichtige Interessen (Art. 6 Abs. 1 lit. d)',
  [LegalBasis.PUBLIC_INTEREST]: 'Öffentliches Interesse (Art. 6 Abs. 1 lit. e)',
  [LegalBasis.LEGITIMATE_INTEREST]: 'Berechtigtes Interesse (Art. 6 Abs. 1 lit. f)'
};

const statusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

const riskConfig: Record<RiskLevel, { label: string; state: string }> = {
  [RiskLevel.LOW]: { label: 'Niedrig', state: 'Success' },
  [RiskLevel.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [RiskLevel.HIGH]: { label: 'Hoch', state: 'Error' }
};

const spRoleConfig: Record<ServiceProviderRole, { label: string; state: string }> = {
  [ServiceProviderRole.PROCESSOR]: { label: 'Auftragsverarbeiter', state: 'Information' },
  [ServiceProviderRole.JOINT_CONTROLLER]: { label: 'Gem. Verantwortlicher', state: 'Warning' },
  [ServiceProviderRole.RECIPIENT]: { label: 'Empfänger', state: 'None' }
};

const dataCategoryLabels: Record<DataCategory, string> = {
  [DataCategory.CONTACT]: 'Kontaktdaten',
  [DataCategory.IDENTIFICATION]: 'Identifikationsdaten',
  [DataCategory.FINANCIAL]: 'Finanzdaten',
  [DataCategory.EMPLOYMENT]: 'Beschäftigungsdaten',
  [DataCategory.HEALTH]: 'Gesundheitsdaten',
  [DataCategory.BEHAVIORAL]: 'Verhaltensdaten',
  [DataCategory.LOCATION]: 'Standortdaten',
  [DataCategory.COMMUNICATION]: 'Kommunikationsdaten',
  [DataCategory.OTHER]: 'Sonstige'
};

const dataSubjectLabels: Record<DataSubjectCategory, string> = {
  [DataSubjectCategory.EMPLOYEES]: 'Mitarbeiter',
  [DataSubjectCategory.CUSTOMERS]: 'Kunden',
  [DataSubjectCategory.PROSPECTS]: 'Interessenten',
  [DataSubjectCategory.SUPPLIERS]: 'Lieferanten',
  [DataSubjectCategory.APPLICANTS]: 'Bewerber',
  [DataSubjectCategory.WEBSITE_VISITORS]: 'Website-Besucher',
  [DataSubjectCategory.BUSINESS_PARTNERS]: 'Geschäftspartner',
  [DataSubjectCategory.OTHER]: 'Sonstige'
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', padding: '0.5rem 0', borderBottom: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
    <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', width: '180px', flexShrink: 0 }}>{label}</Text>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

export default function ProcessingDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEdit, canDelete, canApprove } = useUserRole();
  const [activity, setActivity] = useState<ProcessingActivity | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [riskEditing, setRiskEditing] = useState(false);
  const [linkedProviders, setLinkedProviders] = useState<ServiceProvider[]>([]);
  const [allProviders, setAllProviders] = useState<ServiceProvider[]>([]);
  const [showProviderLinkPanel, setShowProviderLinkPanel] = useState(false);
  const [quickCreateType, setQuickCreateType] = useState<string | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    const orgsResponse = await organizationApi.getAll();
    if (orgsResponse.success && orgsResponse.data && orgsResponse.data.length > 0) {
      const org = orgsResponse.data[0];
      setCurrentOrg(org);
      if (id) {
        const activityId = parseInt(id);
        const [activityRes, linkedProvidersRes, allProvidersRes] = await Promise.all([
          processingApi.getById(activityId, org.id),
          serviceProviderApi.getByProcessingActivity(activityId, org.id),
          serviceProviderApi.getByOrganization(org.id)
        ]);
        if (activityRes.success && activityRes.data) {
          setActivity(activityRes.data);
        } else {
          navigate('/processing');
        }
        if (linkedProvidersRes.success && linkedProvidersRes.data) {
          setLinkedProviders(linkedProvidersRes.data);
        }
        if (allProvidersRes.success && allProvidersRes.data) {
          setAllProviders(allProvidersRes.data);
        }
      }
    }
    setLoading(false);
  };

  const handleEdit = () => navigate(`/processing/${id}/edit`);

  const handleDelete = async () => {
    if (!activity || !currentOrg) return;
    const response = await processingApi.delete(activity.id, currentOrg.id);
    if (response.success) navigate('/processing');
  };

  const handleStatusChange = async (newStatus: ProcessingStatus) => {
    if (!activity || !currentOrg) return;
    const response = await processingApi.updateStatus(activity.id, currentOrg.id, { status: newStatus });
    if (response.success && response.data) setActivity(response.data);
  };

  const handleRiskChange = async (newRisk: RiskLevel) => {
    if (!activity || !currentOrg) return;
    const response = await processingApi.update(activity.id, currentOrg.id, {
      risk_level: newRisk
    });
    if (response.success && response.data) {
      setActivity(response.data);
      setRiskEditing(false);
    }
  };

  const handleLinkProvider = async (providerId: number) => {
    if (!activity || !currentOrg) return;
    const response = await serviceProviderApi.linkActivity(providerId, activity.id, currentOrg.id);
    if (response.success) {
      const linked = allProviders.find(p => p.id === providerId);
      if (linked) setLinkedProviders(prev => [...prev, linked]);
    }
  };

  const handleUnlinkProvider = async (providerId: number) => {
    if (!activity || !currentOrg) return;
    const response = await serviceProviderApi.unlinkActivity(providerId, activity.id, currentOrg.id);
    if (response.success) {
      setLinkedProviders(prev => prev.filter(p => p.id !== providerId));
    }
  };

  const unlinkedProviders = allProviders.filter(
    p => !linkedProviders.some(l => l.id === p.id)
  );

  if (loading || !activity) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  const renderOverviewTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Basic Info */}
      <Card header={<CardHeader titleText="Grundinformationen" avatar={<Icon name="hint" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <InfoRow label="Name"><Text style={{ fontWeight: '500' }}>{activity.name}</Text></InfoRow>
          <InfoRow label="Abteilung"><Text>{activity.department}</Text></InfoRow>
          <InfoRow label="Beschreibung"><Text>{activity.description || '-'}</Text></InfoRow>
          <InfoRow label="Status">
            <ObjectStatus state={statusConfig[activity.status].state as any} showDefaultIcon>
              {statusConfig[activity.status].label}
            </ObjectStatus>
          </InfoRow>
          <InfoRow label="Version"><Text>v{activity.version}</Text></InfoRow>
          <InfoRow label="Erstellt"><Text>{new Date(activity.created_at).toLocaleString('de-DE')}</Text></InfoRow>
          <InfoRow label="Aktualisiert"><Text>{new Date(activity.updated_at).toLocaleString('de-DE')}</Text></InfoRow>
          {activity.approved_at && (
            <InfoRow label="Freigegeben"><Text>{new Date(activity.approved_at).toLocaleString('de-DE')}</Text></InfoRow>
          )}
        </div>
      </Card>

      {/* Purpose */}
      <Card header={<CardHeader titleText="Zweck und Rechtsgrundlage" avatar={<Icon name="official-document" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <InfoRow label="Zweck"><Text>{activity.purpose}</Text></InfoRow>
          <InfoRow label="Rechtsgrundlage"><Text>{legalBasisLabels[activity.legal_basis]}</Text></InfoRow>
          {activity.legal_basis_details && (
            <InfoRow label="Details"><Text>{activity.legal_basis_details}</Text></InfoRow>
          )}
        </div>
      </Card>

      {/* Risk */}
      <Card header={
        <CardHeader
          titleText="Risikobewertung"
          avatar={<Icon name="shield" />}
          action={
            activity.status !== ProcessingStatus.ARCHIVED ? (
              <Button design="Transparent" icon={riskEditing ? 'decline' : 'edit'} onClick={() => setRiskEditing(!riskEditing)}>
                {riskEditing ? 'Abbrechen' : 'Anpassen'}
              </Button>
            ) : undefined
          }
        />
      }>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          {riskEditing ? (
            <div>
              <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.75rem' }}>
                Risikolevel auswählen:
              </Text>
              <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                {Object.entries(riskConfig).map(([key, config]) => (
                  <Button
                    key={key}
                    design={activity.risk_level === key ? 'Emphasized' : 'Default'}
                    onClick={() => handleRiskChange(key as RiskLevel)}
                    icon={key === RiskLevel.LOW ? 'status-positive' : key === RiskLevel.MEDIUM ? 'status-critical' : 'status-negative'}
                  >
                    {config.label}
                  </Button>
                ))}
              </FlexBox>
            </div>
          ) : (
            <>
              <InfoRow label="Risikolevel">
                {activity.risk_level ? (
                  <ObjectStatus state={riskConfig[activity.risk_level].state as any} showDefaultIcon>
                    {riskConfig[activity.risk_level].label}
                  </ObjectStatus>
                ) : (
                  <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>Nicht bewertet</Text>
                )}
              </InfoRow>
              <InfoRow label="TOM-Referenz"><Text>{activity.tom_reference || '-'}</Text></InfoRow>
              <InfoRow label="Drittlandtransfer">
                <ObjectStatus state={activity.third_country_transfer ? 'Warning' : 'Success'} showDefaultIcon>
                  {activity.third_country_transfer ? 'Ja' : 'Nein'}
                </ObjectStatus>
              </InfoRow>
              {activity.third_country_transfer && activity.third_country_details && (
                <InfoRow label="Transfer-Details"><Text>{activity.third_country_details}</Text></InfoRow>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );

  const renderDataTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Card header={<CardHeader titleText="Betroffene Personen" avatar={<Icon name="group" />} />}>
        <FlexBox wrap="Wrap" style={{ gap: '0.5rem', padding: '0.25rem 1.25rem 1.25rem' }}>
          {activity.data_subjects.map(subject => (
            <ObjectStatus key={subject} state="Information" showDefaultIcon>
              {dataSubjectLabels[subject]}
            </ObjectStatus>
          ))}
        </FlexBox>
      </Card>

      <Card header={<CardHeader titleText="Datenkategorien" avatar={<Icon name="database" />} />}>
        <FlexBox wrap="Wrap" style={{ gap: '0.5rem', padding: '0.25rem 1.25rem 1.25rem' }}>
          {activity.data_categories.map(category => (
            <ObjectStatus
              key={category}
              state={category === DataCategory.HEALTH ? 'Warning' : 'Success'}
              showDefaultIcon
            >
              {dataCategoryLabels[category]}
              {category === DataCategory.HEALTH && ' (Art. 9)'}
            </ObjectStatus>
          ))}
        </FlexBox>
        {activity.data_categories.includes(DataCategory.HEALTH) && (
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <MessageStrip design="Warning" hideCloseButton>
              Besondere Kategorien personenbezogener Daten gemäß Art. 9 DSGVO
            </MessageStrip>
          </div>
        )}
      </Card>

      <Card header={<CardHeader titleText="Empfänger" avatar={<Icon name="shipping-status" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          {activity.recipients.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {activity.recipients.map((recipient, i) => (
                <FlexBox key={i} alignItems="Center" style={{ gap: '0.5rem', padding: '0.375rem 0', borderBottom: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
                  <Icon name="employee" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text>{recipient}</Text>
                </FlexBox>
              ))}
            </div>
          ) : (
            <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>Keine Empfänger angegeben</Text>
          )}
        </div>
      </Card>
    </div>
  );

  const renderRetentionTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Card header={<CardHeader titleText="Löschfristen und Aufbewahrung" avatar={<Icon name="history" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <InfoRow label="Löschfrist">
            <Text style={{ fontWeight: '500' }}>
              {formatRetention(activity.retention_period)}
            </Text>
          </InfoRow>
          {activity.retention_details && (
            <InfoRow label="Details"><Text>{activity.retention_details}</Text></InfoRow>
          )}
          <InfoRow label="Löschdatum (ca.)">
            <Text style={{ fontStyle: 'italic', color: 'var(--sapNeutralTextColor)' }}>
              {new Date(
                new Date(activity.created_at).setMonth(
                  new Date(activity.created_at).getMonth() + activity.retention_period
                )
              ).toLocaleDateString('de-DE')}
            </Text>
          </InfoRow>
        </div>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <MessageStrip design="Information" hideCloseButton>
            Die Löschfrist beginnt in der Regel mit dem Ende der Verarbeitung oder der Aufbewahrungspflicht.
          </MessageStrip>
        </div>
      </Card>
    </div>
  );

  const renderServiceProviderTab = () => {
    const getAvvDisplayStatus = (provider: ServiceProvider) => {
      if (!provider.avv_contracts || provider.avv_contracts.length === 0) {
        if (provider.role === ServiceProviderRole.PROCESSOR) {
          return { label: 'Kein AVV', state: 'Warning' };
        }
        return { label: '-', state: 'None' };
      }
      const active = provider.avv_contracts.find(c => c.status === AvvStatus.ACTIVE);
      if (active) return { label: 'Aktiv', state: 'Success' };
      const draft = provider.avv_contracts.find(c => c.status === AvvStatus.DRAFT);
      if (draft) return { label: 'Entwurf', state: 'None' };
      return { label: '-', state: 'None' };
    };

    return (
      <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <FlexBox justifyContent="End" style={{ gap: '0.5rem' }}>
          <Button
            icon="add"
            design="Emphasized"
            onClick={() => { setQuickCreateType('service-provider'); setQuickCreateOpen(true); }}
          >
            Neuer Dienstleister
          </Button>
          <Button
            icon="chain-link"
            onClick={() => setShowProviderLinkPanel(true)}
            disabled={unlinkedProviders.length === 0}
          >
            Dienstleister verknüpfen
          </Button>
        </FlexBox>

        {linkedProviders.length === 0 ? (
          <Card>
            <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
              <Icon name="supplier" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
              <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
                Keine verknüpften Dienstleister
              </Text>
            </div>
          </Card>
        ) : (
          <Card>
            <Table
              columns={
                <>
                  <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Name</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Rolle</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Standort</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>AVV-Status</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Aktionen</Text></TableColumn>
                </>
              }
            >
              {linkedProviders.map(provider => {
                const avvDisplay = getAvvDisplayStatus(provider);
                return (
                  <TableRow
                    key={provider.id}
                    type="Active"
                    onClick={() => navigate(`/service-providers/${provider.id}`)}
                  >
                    <TableCell>
                      <Text style={{ fontWeight: '500', color: 'var(--sapLinkColor)' }}>{provider.name}</Text>
                    </TableCell>
                    <TableCell>
                      <ObjectStatus state={spRoleConfig[provider.role].state as any} showDefaultIcon>
                        {spRoleConfig[provider.role].label}
                      </ObjectStatus>
                    </TableCell>
                    <TableCell>
                      <Text>{provider.hosting_location || '-'}</Text>
                    </TableCell>
                    <TableCell>
                      {avvDisplay.label !== '-' ? (
                        <ObjectStatus state={avvDisplay.state as any} showDefaultIcon>
                          {avvDisplay.label}
                        </ObjectStatus>
                      ) : (
                        <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic', fontSize: '0.8125rem' }}>-</Text>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        design="Transparent"
                        icon="chain-link"
                        style={{ color: 'var(--sapNegativeColor)' }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          handleUnlinkProvider(provider.id);
                        }}
                      >
                        Entfernen
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </Card>
        )}

        {/* Inline link panel */}
        {showProviderLinkPanel && (
          <Card header={<CardHeader titleText="Dienstleister verknüpfen" avatar={<Icon name="chain-link" />} />}>
            <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
              {unlinkedProviders.length === 0 ? (
                <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
                  Alle Dienstleister sind bereits verknüpft.
                </Text>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {unlinkedProviders.map(provider => (
                    <FlexBox
                      key={provider.id}
                      alignItems="Center"
                      justifyContent="SpaceBetween"
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.375rem',
                        border: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                        background: 'var(--sapBaseColor)'
                      }}
                    >
                      <div>
                        <Text style={{ fontWeight: '500', display: 'block' }}>{provider.name}</Text>
                        <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                          {spRoleConfig[provider.role].label}{provider.hosting_location ? ` · ${provider.hosting_location}` : ''}
                        </Text>
                      </div>
                      <Button
                        icon="add"
                        design="Emphasized"
                        onClick={() => handleLinkProvider(provider.id)}
                      >
                        Verknüpfen
                      </Button>
                    </FlexBox>
                  ))}
                </div>
              )}
              <FlexBox justifyContent="End" style={{ marginTop: '1rem' }}>
                <Button design="Transparent" onClick={() => setShowProviderLinkPanel(false)}>Schließen</Button>
              </FlexBox>
            </div>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="page-layout--detail">
      {/* Back link */}
      <Button icon="nav-back" design="Transparent" onClick={() => navigate('/processing')} style={{ marginBottom: '0.75rem' }}>
        Zurück zur Übersicht
      </Button>

      {/* Object Header */}
      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <FlexBox justifyContent="SpaceBetween" alignItems="Start" wrap="Wrap" style={{ gap: '1rem' }}>
            <div style={{ flex: '1 1 auto' }}>
              <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.5rem' }}>
                <Title level="H3" style={{ margin: 0 }}>{activity.name}</Title>
                <ObjectStatus state={statusConfig[activity.status].state as any} showDefaultIcon>
                  {statusConfig[activity.status].label}
                </ObjectStatus>
              </FlexBox>
              {/* Key attributes */}
              <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginTop: '0.75rem' }}>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="factory" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>{activity.department}</Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="official-document" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>{legalBasisLabels[activity.legal_basis].split(' (')[0]}</Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="shield" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  {activity.risk_level ? (
                    <ObjectStatus state={riskConfig[activity.risk_level].state as any} showDefaultIcon>
                      {riskConfig[activity.risk_level].label}
                    </ObjectStatus>
                  ) : (
                    <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>Risiko: -</Text>
                  )}
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="calendar" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>v{activity.version} · {new Date(activity.updated_at).toLocaleDateString('de-DE')}</Text>
                </FlexBox>
              </FlexBox>
            </div>

            {/* Actions */}
            <FlexBox style={{ gap: '0.5rem' }} wrap="Wrap">
              <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('processing/' + id, currentOrg.id)}>PDF</Button>
              {canEdit && activity.status !== ProcessingStatus.ARCHIVED && (
                <Button icon="edit" onClick={handleEdit}>Bearbeiten</Button>
              )}
              {canEdit && activity.status === ProcessingStatus.DRAFT && (
                <Button design="Emphasized" icon="paper-plane" onClick={() => handleStatusChange(ProcessingStatus.IN_REVIEW)}>
                  Zur Prüfung
                </Button>
              )}
              {activity.status === ProcessingStatus.IN_REVIEW && (
                <>
                  {canEdit && <Button design="Transparent" icon="undo" onClick={() => handleStatusChange(ProcessingStatus.DRAFT)}>Zurück</Button>}
                  {canApprove && <Button design="Emphasized" icon="accept" onClick={() => handleStatusChange(ProcessingStatus.APPROVED)}>Freigeben</Button>}
                </>
              )}
              {activity.status === ProcessingStatus.APPROVED && (
                <>
                  {canApprove && <Button design="Transparent" icon="undo" onClick={() => handleStatusChange(ProcessingStatus.DRAFT)}>Zurück</Button>}
                  {canApprove && <Button design="Attention" icon="archive" onClick={() => handleStatusChange(ProcessingStatus.ARCHIVED)}>Archivieren</Button>}
                </>
              )}
              {canEdit && activity.status === ProcessingStatus.ARCHIVED && (
                <Button design="Emphasized" icon="undo" onClick={() => handleStatusChange(ProcessingStatus.DRAFT)}>Reaktivieren</Button>
              )}
              {canDelete && <Button icon="delete" design="Negative" onClick={() => setShowDeleteDialog(true)}>Löschen</Button>}
            </FlexBox>
          </FlexBox>
        </div>
      </Card>

      {/* Tabs */}
      <TabContainer>
        <Tab text="Überblick" icon="hint" selected>
          {renderOverviewTab()}
        </Tab>
        <Tab text="Daten & Betroffene" icon="group">
          {renderDataTab()}
        </Tab>
        <Tab text="Löschfristen" icon="history">
          {renderRetentionTab()}
        </Tab>
        <Tab text="Dienstleister" icon="supplier">
          {renderServiceProviderTab()}
        </Tab>
      </TabContainer>

      {/* Delete Dialog */}
      {showDeleteDialog && (
        <MessageBox
          open={showDeleteDialog}
          onClose={(e: any) => {
            setShowDeleteDialog(false);
            if (e.detail.action === 'OK') handleDelete();
          }}
          type="Confirm"
          titleText="Verarbeitung löschen?"
        >
          Möchten Sie diese Verarbeitung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </MessageBox>
      )}

      {quickCreateType && currentOrg && (
        <QuickCreateDialog
          type={quickCreateType as 'processing' | 'service-provider' | 'tom' | 'dsr' | 'breach'}
          organizationId={currentOrg.id}
          open={quickCreateOpen}
          onClose={() => setQuickCreateOpen(false)}
          onCreated={() => {
            setQuickCreateOpen(false);
            if (quickCreateType === 'service-provider') {
              // Reload to show the new provider in the linkable list
              loadData();
            }
          }}
        />
      )}
    </div>
  );
}
